import React, { useState, useEffect, useMemo } from 'react';
import { supabaseAdmin } from '../../../lib/supabaseAdmin';
import { Code, Plus, Trash2, Save, CheckCircle, XCircle, AlertCircle, Search, ChevronDown } from 'lucide-react';

interface GTMClasses {
  [bankId: string]: string;
}

interface PageTracking {
  id: string;
  page_slug: string;
  page_name: string;
  gtm_classes: GTMClasses;
  enable_gtm: boolean;
}

interface AvailableBank {
  bank_id: string;
  bank_name: string;
  button_id: string;
}

const GTMClassManager = () => {
  const [pages, setPages] = useState<PageTracking[]>([]);
  const [selectedPage, setSelectedPage] = useState<PageTracking | null>(null);
  const [gtmClasses, setGtmClasses] = useState<{ bankId: string; className: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'general' | 'bank-details' | 'credit-pages'>('all');
  const [availableBanks, setAvailableBanks] = useState<AvailableBank[]>([]);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .select('id, page_slug, page_name, gtm_classes, enable_gtm')
        .order('page_name', { ascending: true });

      if (error) throw error;
      setPages(data || []);

      if (data && data.length > 0) {
        selectPage(data[0]);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      setErrorMessage('Sayfalar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const selectPage = async (page: PageTracking) => {
    setSelectedPage(page);

    const classes = page.gtm_classes || {};
    const classArray = Object.entries(classes).map(([bankId, className]) => ({
      bankId,
      className,
    }));

    setGtmClasses(classArray.length > 0 ? classArray : [{ bankId: '', className: '' }]);

    await fetchAvailableBanks(page.page_slug);
  };

  const fetchAvailableBanks = async (pageSlug: string) => {
    try {
      const banks: AvailableBank[] = [];

      if (pageSlug === '/uzaktan-musteri-edinimi') {
        const { data, error } = await supabaseAdmin
          .from('bank_offers')
          .select('bank_id, bank_name')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (!error && data && data.length > 0) {
          data.forEach(bank => {
            banks.push({
              bank_id: bank.bank_id,
              bank_name: bank.bank_name,
              button_id: `remote-customer-${bank.bank_id}`
            });
          });
        } else {
          const hardcodedBanks = [
            { bank_id: 'getir-finans', bank_name: 'Getir Finans' },
            { bank_id: 'akbank', bank_name: 'Akbank' },
            { bank_id: 'garantibbva', bank_name: 'Garanti BBVA' },
            { bank_id: 'qnb', bank_name: 'QNB' },
            { bank_id: 'nkolay', bank_name: 'Nkolay' },
            { bank_id: 'enpara', bank_name: 'Enpara' },
            { bank_id: 'isbankasi', bank_name: 'İş Bankası' },
            { bank_id: 'yapikredi', bank_name: 'Yapı Kredi' }
          ];

          hardcodedBanks.forEach(bank => {
            banks.push({
              bank_id: bank.bank_id,
              bank_name: bank.bank_name,
              button_id: `remote-customer-${bank.bank_id}`
            });
          });
        }
      } else if (pageSlug.startsWith('/banka/')) {
        const bankSlug = pageSlug.replace('/banka/', '');

        // Banka bilgisini al
        const { data: bankInfo, error: bankError } = await supabaseAdmin
          .from('bank_campaigns')
          .select('bank_id, bank_name')
          .eq('bank_id', bankSlug)
          .eq('is_active', true)
          .maybeSingle();

        let bankData = bankInfo;

        // Fallback: Eğer bank_campaigns'te yoksa manuel oluştur
        if (!bankInfo || bankError) {
          const bankNames: { [key: string]: string } = {
            'enpara': 'Enpara',
            'nkolay': 'Nkolay',
            'akbank': 'Akbank',
            'garantibbva': 'Garanti BBVA',
            'getir-finans': 'Getir Finans',
            'isbankasi': 'İş Bankası',
            'qnb': 'QNB',
            'yapikredi': 'Yapı Kredi'
          };

          if (bankNames[bankSlug]) {
            bankData = {
              bank_id: bankSlug,
              bank_name: bankNames[bankSlug]
            };
          }
        }

        if (bankData) {
          // Hero butonu
          banks.push({
            bank_id: `${bankData.bank_id}-hero`,
            bank_name: `${bankData.bank_name} - Hero Alan`,
            button_id: `bank-detail-${bankData.bank_id}-hero`
          });

          // Kampanyaları al ve her biri için buton oluştur
          const { data: campaigns } = await supabaseAdmin
            .from('bank_campaigns')
            .select('id, title, display_order')
            .eq('bank_id', bankSlug)
            .eq('is_active', true)
            .order('display_order', { ascending: true });

          if (campaigns && campaigns.length > 0) {
            campaigns.forEach((campaign, index) => {
              banks.push({
                bank_id: `${bankData.bank_id}-campaign-${campaign.display_order}`,
                bank_name: `${bankData.bank_name} - Kampanya ${campaign.display_order}`,
                button_id: `bank-detail-${bankData.bank_id}-campaign-${campaign.display_order}`
              });
            });
          }

          // "Kampanya Yok" butonu
          banks.push({
            bank_id: `${bankData.bank_id}-no-campaigns`,
            bank_name: `${bankData.bank_name} - Kampanya Yok`,
            button_id: `bank-detail-${bankData.bank_id}-no-campaigns`
          });

          // Kredi butonları
          const { data: credits } = await supabaseAdmin
            .from('bank_credits')
            .select('id, title, display_order')
            .eq('bank_id', bankSlug)
            .eq('is_active', true)
            .order('display_order', { ascending: true });

          if (credits && credits.length > 0) {
            credits.forEach((credit) => {
              banks.push({
                bank_id: `${bankData.bank_id}-credit-${credit.display_order}`,
                bank_name: `${bankData.bank_name} - Kredi ${credit.display_order}`,
                button_id: `bank-detail-${bankData.bank_id}-credit-${credit.display_order}`
              });
            });
          } else {
            banks.push({
              bank_id: `${bankData.bank_id}-no-credits`,
              bank_name: `${bankData.bank_name} - Kredi Yok`,
              button_id: `bank-detail-${bankData.bank_id}-no-credits`
            });
          }

          // Kart butonları
          const { data: cards } = await supabaseAdmin
            .from('bank_cards')
            .select('id, title, display_order')
            .eq('bank_id', bankSlug)
            .eq('is_active', true)
            .order('display_order', { ascending: true });

          if (cards && cards.length > 0) {
            cards.forEach((card) => {
              banks.push({
                bank_id: `${bankData.bank_id}-card-${card.display_order}`,
                bank_name: `${bankData.bank_name} - Kart ${card.display_order}`,
                button_id: `bank-detail-${bankData.bank_id}-card-${card.display_order}`
              });
            });
          } else {
            banks.push({
              bank_id: `${bankData.bank_id}-no-cards`,
              bank_name: `${bankData.bank_name} - Kart Yok`,
              button_id: `bank-detail-${bankData.bank_id}-no-cards`
            });
          }
        }
      } else if (pageSlug.includes('-kredisi')) {
        const loanType = pageSlug.substring(1);
        const { data, error } = await supabaseAdmin
          .from('featured_loans')
          .select('bank_id, bank_name, loan_type')
          .eq('loan_type', loanType)
          .eq('is_active', true);

        if (!error && data) {
          const uniqueBanks = new Map<string, AvailableBank>();
          data.forEach(loan => {
            if (!uniqueBanks.has(loan.bank_id)) {
              uniqueBanks.set(loan.bank_id, {
                bank_id: loan.bank_id,
                bank_name: loan.bank_name,
                button_id: `credit-loan-${loan.bank_id}-${loan.loan_type}`
              });
            }
          });
          banks.push(...Array.from(uniqueBanks.values()));
        }
      }

      setAvailableBanks(banks);
    } catch (error) {
      console.error('Error fetching available banks:', error);
      setAvailableBanks([]);
    }
  };

  const handleAddClass = () => {
    setGtmClasses([...gtmClasses, { bankId: '', className: '' }]);
  };

  const handleRemoveClass = (index: number) => {
    const newClasses = gtmClasses.filter((_, i) => i !== index);
    setGtmClasses(newClasses.length > 0 ? newClasses : [{ bankId: '', className: '' }]);
  };

  const handleClassChange = (index: number, field: 'bankId' | 'className', value: string) => {
    const newClasses = [...gtmClasses];
    newClasses[index][field] = value;
    setGtmClasses(newClasses);
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    try {
      setIsSaving(true);

      const gtmClassesObj: GTMClasses = {};
      gtmClasses.forEach(({ bankId, className }) => {
        if (bankId && className) {
          gtmClassesObj[bankId] = className;
        }
      });

      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .update({
          gtm_classes: gtmClassesObj,
          enable_gtm: selectedPage.enable_gtm,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPage.id);

      if (error) throw error;

      setSuccessMessage('GTM class\'ları başarıyla kaydedildi');
      setTimeout(() => setSuccessMessage(null), 3000);

      await fetchPages();
    } catch (error) {
      console.error('Error saving GTM classes:', error);
      setErrorMessage('Kaydetme sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleGTM = async () => {
    if (!selectedPage) return;

    try {
      const newValue = !selectedPage.enable_gtm;

      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .update({
          enable_gtm: newValue,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPage.id);

      if (error) throw error;

      setSelectedPage({ ...selectedPage, enable_gtm: newValue });
      setSuccessMessage(`GTM tracking ${newValue ? 'aktif' : 'pasif'} edildi`);
      setTimeout(() => setSuccessMessage(null), 3000);

      await fetchPages();
    } catch (error) {
      console.error('Error toggling GTM:', error);
      setErrorMessage('İşlem sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const categorizedPages = useMemo(() => {
    const creditPages = pages.filter(p =>
      p.page_slug.includes('-kredisi')
    );
    const bankDetails = pages.filter(p => p.page_slug.startsWith('/banka/'));
    const general = pages.filter(p =>
      !p.page_slug.startsWith('/banka/') &&
      !p.page_slug.includes('-kredisi')
    );

    return {
      general,
      bankDetails,
      creditPages,
    };
  }, [pages]);

  const filteredPages = useMemo(() => {
    let filtered = pages;

    if (selectedCategory === 'general') {
      filtered = categorizedPages.general;
    } else if (selectedCategory === 'bank-details') {
      filtered = categorizedPages.bankDetails;
    } else if (selectedCategory === 'credit-pages') {
      filtered = categorizedPages.creditPages;
    }

    if (searchTerm) {
      filtered = filtered.filter(
        p =>
          p.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.page_slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [pages, selectedCategory, searchTerm, categorizedPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F7BA45] mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Code className="h-7 w-7 text-[#F7BA45]" />
          <h1 className="text-2xl font-bold text-slate-800">
            GTM Class Yönetimi
          </h1>
        </div>
        <p className="text-slate-600">
          Google Tag Manager tetikleyicileri için banka butonlarına özel CSS class'ları tanımlayın
        </p>
      </div>

      {/* Alert Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{errorMessage}</span>
        </div>
      )}

      {/* Info Box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">GTM Tetikleyici Nasıl Oluşturulur?</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Google Tag Manager'da Tetikleyiciler → Yeni</li>
              <li>Tetikleyici Türü: Tıklama - Tüm Öğeler</li>
              <li>Tetikleyici şu koşulda etkinleşir: Bazı Tıklamalar</li>
              <li>Click Classes → şunu içerir → <code className="bg-blue-100 px-1 rounded">Akbank_teklif</code></li>
              <li>Tag'inizi bu tetikleyiciye bağlayın</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Page Selector with Dropdown */}
      <div className="mb-6">
        <div className="flex flex-col gap-3">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#F7BA45] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Tümü ({pages.length})
            </button>
            <button
              onClick={() => setSelectedCategory('general')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'general'
                  ? 'bg-[#F7BA45] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Genel Sayfalar ({categorizedPages.general.length})
            </button>
            <button
              onClick={() => setSelectedCategory('credit-pages')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'credit-pages'
                  ? 'bg-[#F7BA45] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Kredi Sayfaları ({categorizedPages.creditPages.length})
            </button>
            <button
              onClick={() => setSelectedCategory('bank-details')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'bank-details'
                  ? 'bg-[#F7BA45] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Banka Detayları ({categorizedPages.bankDetails.length})
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Sayfa ara..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
            />
          </div>
        </div>

        {/* Page Dropdown */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Sayfa Seçin ({filteredPages.length} sayfa)
          </label>
          <select
            value={selectedPage?.id || ''}
            onChange={(e) => {
              const page = pages.find(p => p.id === e.target.value);
              if (page) selectPage(page);
            }}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent bg-white"
          >
            <option value="">Bir sayfa seçin...</option>
            {filteredPages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.page_name} - {page.page_slug} {page.enable_gtm ? '✓ GTM Aktif' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* GTM Classes Editor */}
        <div>
          {selectedPage ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  {selectedPage.page_name} - Banka Class'ları
                </h3>
                <button
                  onClick={toggleGTM}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPage.enable_gtm
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {selectedPage.enable_gtm ? 'GTM Aktif' : 'GTM Pasif'}
                </button>
              </div>

              {availableBanks.length > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    Bu sayfada {availableBanks.length} banka bulundu:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableBanks.map(bank => (
                      <span key={bank.bank_id} className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs">
                        {bank.bank_name}
                        <code className="ml-2 text-xs text-blue-600">ID: {bank.button_id}</code>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {gtmClasses.map((item, index) => {
                  const selectedBank = availableBanks.find(b => b.bank_id === item.bankId);
                  return (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Banka Seç
                          </label>
                          <select
                            value={item.bankId}
                            onChange={(e) => handleClassChange(index, 'bankId', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent bg-white"
                          >
                            <option value="">Banka seçin...</option>
                            {availableBanks.map(bank => (
                              <option key={bank.bank_id} value={bank.bank_id}>
                                {bank.bank_name}
                              </option>
                            ))}
                          </select>
                          {selectedBank && (
                            <p className="mt-1 text-xs text-slate-500">
                              Button ID: <code className="bg-slate-100 px-1 rounded">{selectedBank.button_id}</code>
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            CSS Class
                          </label>
                          <input
                            type="text"
                            value={item.className}
                            onChange={(e) => handleClassChange(index, 'className', e.target.value)}
                            placeholder="Akbank_teklif"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveClass(index)}
                        disabled={gtmClasses.length === 1}
                        className="mt-6 p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}

                <button
                  onClick={handleAddClass}
                  className="w-full px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-[#F7BA45] hover:text-[#F7BA45] transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Yeni Banka Ekle
                </button>
              </div>

              {/* Preview */}
              {gtmClasses.some(c => c.bankId && c.className) && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Önizleme</h4>
                  <div className="space-y-2">
                    {gtmClasses
                      .filter(c => c.bankId && c.className)
                      .map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <code className="bg-white px-2 py-1 rounded border border-slate-300 font-mono text-xs">
                            id="remote-customer-{item.bankId}"
                          </code>
                          <span className="text-slate-400">→</span>
                          <code className="bg-white px-2 py-1 rounded border border-slate-300 font-mono text-xs">
                            class="{item.className}"
                          </code>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-3 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-5 w-5" />
                  {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Bir sayfa seçin
            </div>
          )}
        </div>
      </div>

      {/* Example Section */}
      <div className="mt-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Örnek Kullanım
        </h3>
        <div className="space-y-4 text-sm text-slate-700">
          <div>
            <p className="font-medium mb-2">1. Admin Panelde Class Tanımlayın:</p>
            <div className="bg-white p-3 rounded border border-slate-300">
              <p className="font-mono text-xs">
                Banka ID: <span className="text-blue-600">akbank</span><br />
                CSS Class: <span className="text-green-600">Akbank_teklif</span>
              </p>
            </div>
          </div>
          <div>
            <p className="font-medium mb-2">2. Sayfada Otomatik Uygulanır:</p>
            <div className="bg-white p-3 rounded border border-slate-300">
              <code className="text-xs">
                {`<button id="remote-customer-akbank" class="Akbank_teklif">`}
              </code>
            </div>
          </div>
          <div>
            <p className="font-medium mb-2">3. GTM'de Tetikleyici Oluşturun:</p>
            <div className="bg-white p-3 rounded border border-slate-300 space-y-1 text-xs">
              <p>• Tetikleyici Türü: <strong>Tıklama - Tüm Öğeler</strong></p>
              <p>• Koşul: Click Classes → <strong>şunu içerir</strong> → <strong>Akbank_teklif</strong></p>
              <p>• Tag'inizi bu tetikleyiciye bağlayın</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GTMClassManager;
